import {useCallback, useEffect, useState} from "react";
import {Button, ScrollArea, Table} from "@mantine/core";
import GiftBoxAnimation from "./GiftBoxAnimation";
import {toast} from "react-toastify";
import api from '../../api';

interface AdminWinnersTabProps {
    campaignPid: string;
}

interface Winner {
    name: string;
    ordinalNumber: number;
    participantName: string | null;
    participantLastName: string | null;
    participantPhoneNumber: string | null;
    billCode: string | null;
}

export function AdminWinnersTab({ campaignPid }: AdminWinnersTabProps) {
    const [showAnimation, setShowAnimation] = useState(false);
    const [winnerName, setWinnerName] = useState("");
    const [rewardsLeft, setRewardsLeft] = useState(0);
    const [winners, setWinners] = useState<Winner[]>([]);

    const fetchWinners = useCallback(async () => {
        try {
            const response = await api.get(`/admin/campaigns/${campaignPid}/winners`);
            const winnersData = response.data;

            // Backend returns PageApiResponse with content array
            if (winnersData.content && Array.isArray(winnersData.content)) {
                setWinners(winnersData.content);
            } else {
                setWinners([]);
            }
        } catch (error) {
            console.error("Error loading winners:", error);
            toast.error("Neuspešno učitavanje dobitnika.", { position: "top-center" });
        }
    }, [campaignPid]);

    useEffect(() => {
        fetchWinners();
    }, [campaignPid, fetchWinners]);

    const handleDrawWinner = async () => {
        try {
            const response = await api.post(
                `/admin/campaigns/${campaignPid}/winners/generate?next=true`
            );

            const data = response.data;
            setWinnerName(
                `[${data.winners[0].reward.name}] ${data.winners[0].participant.firstName} ${data.winners[0].participant.lastName} ${data.winners[0].participant.billCode}`
            );
            setRewardsLeft(data.rewardsLeft);
            setShowAnimation(true);
            await fetchWinners();
            toast.success("Pobednik uspešno izvučen!", { position: "top-center" });
        } catch (error: any) {
            const err = error?.response?.data?.message || "Greška prilikom izvlačenja pobednika";
            toast.error(err, { position: "top-center" });
        }
    };

    const handleDistributeWinners = async () => {
        try {
            const response = await api.post(
                `/admin/campaigns/${campaignPid}/winners/generate?distribute=true`
            );

            const data = response.data;
            await fetchWinners();
            toast.success(`Uspešno izvučeno ${data.winners?.length || 0} pobednika!`, { position: "top-center" });
        } catch (error: any) {
            const err = error?.response?.data?.message || "Greška prilikom izvlačenja pobednika";
            toast.error(err, { position: "top-center" });
        }
    };

    const handleAnimationFinish = () => {
        setShowAnimation(false);
        toast.info(`Preostalo je jos ${rewardsLeft} nagrada.`);
    };

    const winnerTableRows = winners.map((winner, index) => (
        <Table.Tr key={index}>
            <Table.Td>{winner.name}</Table.Td>
            <Table.Td>{winner.participantName || "-"}</Table.Td>
            <Table.Td>{winner.participantLastName || "-"}</Table.Td>
            <Table.Td>{winner.participantPhoneNumber || "-"}</Table.Td>
            <Table.Td>{winner.billCode || "-"}</Table.Td>
        </Table.Tr>
    ));

    return (
        <>
            {showAnimation && (
                <GiftBoxAnimation onFinish={handleAnimationFinish} text={winnerName} />
            )}

            <ScrollArea className="w-full flex flex-col">
                <div className="flex justify-evenly items-center p-5 my-10">
                    <Button variant="filled" size="md" color="teal" onClick={handleDrawWinner}>
                        Izvuci dobitnika
                    </Button>
                    <Button variant="filled" size="md" color="teal" onClick={handleDistributeWinners}>
                        Izvuci preostale dobitnike
                    </Button>
                </div>
                <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Naziv nagrade</Table.Th>
                            <Table.Th>Ime ucesnika</Table.Th>
                            <Table.Th>Prezime ucesnika</Table.Th>
                            <Table.Th>Broj telefona</Table.Th>
                            <Table.Th>PFR Broj racuna</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{winnerTableRows}</Table.Tbody>
                </Table>
            </ScrollArea>
        </>
    );
}
